// @flow
import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import idx from "idx";
import Media from "react-media";
import styled, { css } from "styled-components";
import throttle from "lodash/throttle";
import Gettext from "../common/Gettext";
import Scrollable from "../common/Scrollable";
import TournamentApi from "../Tournaments/Api";
import ClickOutside from "../common/ClickOutside";
import { Fade } from "../common/Animations";
import withData from "../withData";
import List from "./List";
import { ModalConsumer } from "../common/ModalProvider";
import Spinner from "../Spinners/SimpleSpinner";
import { fontSize } from "../common/styled-mixins";
import { get } from "../common/fetch";

import type { Tournament as TournamentProps } from "../../redux/CommonTypes";

// TODO: refactor icons file in /components and get back to this import after
import _BellIcon from "../Icons/Bell";
import { gaReducer } from "../GA";
import ThemeProvider from "../ThemeProvider";

const Container = styled.div`
  margin-bottom: 3px;
  @media (min-width: 960px) {
    position: relative;
  }
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  color: ${({ theme }) => theme.navigationItemColor};

  transition: all 200ms ease-in-out;

  ${({ isIronHeader, active, theme }) =>
    !isIronHeader && active
      ? css`
          background-color: ${theme.navigationItemActiveBg};
        `
      : css`
          background-color: transparent;
        `};
`;

const BellWrapper = styled.div`
  padding: 9px;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "14px"};
  line-height: 19px;
  color: ${({ theme }) => theme.navigationItemColor};
  text-decoration: none;
  width: 100%;
  display: flex;
  cursor: pointer;
  ${({ theme }) => {
    if (theme.direction === "rtl") {
      return css`
        padding-right: 14px;
        padding-left: 11px;
      `;
    }

    return css`
      padding-left: 14px;
      padding-right: 11px;
    `;
  }};

  :hover,
  :focus {
    color: ${props => props.theme.accent};
    text-decoration: none;
    outline: none;
  }
`;

const BellIconWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 19px;
  height: 19px;
  color: ${({ theme }) => theme.navigationItemIcon};
  backface-visibility: hidden;

  ${({ theme }) => {
    if (theme.direction === "rtl") {
      return css`
        margin-left: 10px;
      `;
    }

    return css`
      margin-right: 10px;
    `;
  }};

  ${props =>
    props.active &&
    css`
      color: ${({ theme }) => theme.navigationItemIconActive};
    `};
`;

const BellIcon = styled(_BellIcon)`
  fill: currentColor;

  @media (max-width: 959px) {
    width: 20px;
  }
`;

const BellTitle = styled.div`
  flex-grow: 1;
`;

const Count = styled.div`
  flex: 0 0 auto;
  background-color: #0085eb;
  min-width: 20px;
  padding: 0 5px;
  border-radius: 5px 5px 5px 0;
  color: #fff;
  text-align: center;
  font-size: ${fontSize(12)};
  font-weight: 400;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 364px;
  cursor: default;
  z-index: 200;

  ${({ isIronHeader }) =>
    isIronHeader
      ? css`
          position: absolute;
          top: 100%;
          left: 0;
          max-height: ${({ maxHeight }) => maxHeight}px;
          box-shadow: 0 6px 10px rgba(0, 0, 0, 0.42);
          border-radius: 2px 2px 0 0;

          :after {
            position: absolute;
            top: 0;
            left: 26px;
            content: " ";
            height: 0;
            width: 0;
            margin-left: -8px;
            margin-top: -18px;
            pointer-events: none;
            border: 9px solid transparent;
            border-bottom-color: #fff;
          }

          @media (max-width: 959px) {
            position: absolute;
            top: 62px;
            left: 50%;
            transform: translate3d(-50%, 0, 0);
            backface-visibility: hidden;
            align-self: center;

            :after {
              left: 50%;
              transform: translateX(-50%);
              margin-left: 0;
            }
          }

          @media (max-width: 375px) {
            width: 100vw;
            left: 0;
            transform: none;

            :after {
            }
          }
        `
      : css`
          position: fixed;
          top: 0;
          right: 276px;
          bottom: 0;

          :after {
            position: absolute;
            top: ${({ arrowTop, trade }) =>
              arrowTop && `${arrowTop - (trade ? 74 : 0) + 6}px`};
            right: -26px;
            content: " ";
            height: 0;
            width: 0;
            pointer-events: none;
            border: 13px solid transparent;
            border-left-color: #fff;
          }

          @media (max-width: 959px) {
            top: 0;
            left: 0;
            backface-visibility: hidden;
            align-self: center;
            width: 100vw;
            background-color: #f6f6f6;
            :after {
              display: none;
            }
          }

          @media (max-width: 375px) {
            width: 100vw;
            left: 0;
            transform: none;
          }
        `}
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  color: #fff;
  background-color: #fff;
  overflow: hidden;
  max-width: 400px;
  width: 100%;
  margin: 0 auto;
  @media (max-width: 365px) {
    border-radius: 0;
  }
`;

const Bar = styled.div`
  flex: 1;
  width: 6px;
  margin: 2px;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0.5;
  border-radius: 4px;
`;

const BellHead = styled.div`
  height: 40px;
  padding: 10px 0;
  position: relative;
  background-color: ${({ theme }) =>
    theme.mode === "light" ? "#fff" : "#f6f6f6"};

  @media (max-width: 959px) {
    background-color: #f6f6f6;
  }

  button {
    position: absolute;
    right: 10px;
    top: 10px;
    background: transparent;
    margin: 0;
    padding: 0;
    width: 20px;
    height: 20px;
    border: none;
    cursor: pointer;
    transition: 0.4s;
    :before,
    :after {
      content: "";
      position: absolute;
      top: -1px;
      left: 50%;
      width: 0px;
      height: 20px;
      border-left: 2px solid #919191;
    }

    :before {
      transform: translateX(-50%) rotate(45deg);
      transform-origin: 100%;
    }

    :after {
      transform: translateX(-50%) rotate(-45deg);
      transform-origin: 0%;
    }

    :hover {
      opacity: 0.7;
    }
  }
`;

const BellH = styled.div`
  text-transform: uppercase;
  font-size: 14px;
  line-height: 19px;
  color: #343434;
  text-align: center;
  font-weight: bold;
`;

type ApiProps = {
  errorCode: null | string,
  readEntity: any,
  fetchEntity: any
};

type NewsItem = {|
  title: string,
  createdAt: string,
  slug: string,
  publishedAt?: null | string
|};

export type AnnouncementsProps =
  | {
      id?: number,
      type: "tournament",
      timestamp: number,
      tournament: TournamentProps
    }
  | {
      id?: number,
      type: "news",
      timestamp: number,
      news: NewsItem
    };

type NewsProps = {
  style: string | number,
  buttons: React.Node,
  children: React.Node,
  arrowTop: number
};

const Announcements = ({
  style,
  children,
  close,
  arrowTop,
  isIronHeader,
  api
}: NewsProps) => {
  const [height, setHeight] = useState(304);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  const [announcements, setAnnouncements] = useState(() =>
    idx(api.readEntity("/api/bell-tolls-items"), n => n.value.payload)
  );
  const [loading, setLoading] = useState(false);

  const ref = useRef(null);
  useLayoutEffect(() => {
    if (ref.current != null) {
      const heightCalc = () => {
        const listTop =
          ref.current.getBoundingClientRect().top + window.pageYOffset;
        return windowHeight - listTop - 15;
      };
      setHeight(heightCalc());
    }
  }, [windowHeight]);

  useEffect(() => {
    const windowHeightFunc = throttle(() => {
      setWindowHeight(window.innerHeight);
    }, 150);
    window.addEventListener("resize", windowHeightFunc);
    return () => window.removeEventListener("resize", windowHeightFunc);
  }, []);

  const updateDataFunc = e => {
    const el = e.target;
    const isBottomOfList =
      el.scrollHeight - el.scrollTop - el.clientHeight < 10;
    const currPage = Number(announcements.currPage);
    const notLastPage =
      announcements.pages === 1 ? false : currPage < announcements.pages;

    if (isBottomOfList && !loading && notLastPage) {
      setLoading(true);
      get(
        `/api/bell-tolls-items?page=${
          currPage === 0 ? currPage + 2 : currPage + 1
        }&page_size=10`
      )
        .then(data => {
          if (data.status === "OK") {
            setAnnouncements(prevState => {
              data.payload.data = prevState.data.concat(data.payload.data);
              return data.payload;
            });
          }
        })
        .then(() => setLoading(false));
    }
  };

  return (
    <ThemeProvider themeOverride={"default"}>
      <ListContainer
        isIronHeader={isIronHeader}
        style={style}
        maxHeight={height}
        ref={ref}
        arrowTop={arrowTop}
        trade={window.location.pathname === "/trade"}
      >
        <ListWrapper>
          <BellHead>
            <BellH>
              <Gettext>Notifications</Gettext>
            </BellH>
            <button onClick={() => close()} />
          </BellHead>
          <Scrollable bar={<Bar />} scrollFunc={updateDataFunc}>
            <React.Fragment>
              {children(announcements.data)}
              {loading && (
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: 7
                  }}
                >
                  <Spinner accent />
                </div>
              )}
            </React.Fragment>
          </Scrollable>
        </ListWrapper>
      </ListContainer>
    </ThemeProvider>
  );
};

type Props = {
  modalQueue: Array<{
    component: React.ComponentType<*>,
    props: any
  }>,
  news: Array<NewsItem>,
  helpdesk: any,
  api: ApiProps
};

type State = {
  active: boolean
};

class StatefulBell extends React.Component<Props, State> {
  state = {
    active: false,
    arrowTop: 255
  };

  viewed = () => {
    this.props.api.fetchEntity("/api/notifications/viewed");
  };

  open = () => {
    this.props.onBellClick && this.props.onBellClick(true);
    this.setState({ active: true });
    gaReducer("BELL_OPEN");
    const bellNav = idx(this.bellRef, _ => _.current);
    if (bellNav) {
      this.setState({
        arrowTop: bellNav.getBoundingClientRect().top
      });
    }
  };

  close = () => {
    this.props.onBellClick && this.props.onBellClick(false);
    this.setState(
      {
        active: false
      },
      this.viewed
    );
  };

  componentWillUnmount() {
    if (this.state.active === true) {
      this.viewed();
    }
  }

  bellRef = React.createRef();

  render() {
    if (window.location.pathname === "/password-update") {
      return null;
    }

    const { api } = this.props;

    const unitedNotifications = idx(
      api.readEntity("/api/bell-tolls-items"),
      n => n.value.payload
    );

    if (unitedNotifications == null) {
      return null;
    }

    const new_count = unitedNotifications.new_count;

    const { active } = this.state;
    const disable = !active || this.props.modalQueue.length > 0;

    const announcementsView = (
      <Fade in={active}>
        {({ style }) => (
          <Announcements
            isIronHeader={this.props.isIronHeader}
            style={style}
            close={this.close}
            arrowTop={this.state.arrowTop}
            api={api}
          >
            {announcements => (
              <List
                announcements={announcements}
                api={api}
                close={this.close}
              />
            )}
          </Announcements>
        )}
      </Fade>
    );

    return (
      <ClickOutside onOutsideClick={this.close} disabled={disable}>
        {({ innerRef }) => (
          <Container
            className={this.props.className}
            ref={innerRef}
            noMargin={this.props.noMargin}
          >
            <Inner
              active={active}
              ref={this.bellRef}
              isIronHeader={this.props.isIronHeader}
            >
              <BellWrapper onClick={active ? this.close : this.open}>
                <BellIconWrapper
                  active={
                    active ||
                    (this.props.isIronHeader &&
                      unitedNotifications.data.length > 0)
                  }
                >
                  <BellIcon />
                </BellIconWrapper>

                {!this.props.isIronHeader && (
                  <BellTitle>Notifications</BellTitle>
                )}
                {new_count > 0 ? <Count>{new_count}</Count> : null}
              </BellWrapper>
              <Media
                query="(min-width: 960px)"
                render={() => announcementsView}
              />
            </Inner>
            <Media
              query="(max-width: 959px)"
              render={() => announcementsView}
            />
          </Container>
        )}
      </ClickOutside>
    );
  }
}

const _Bell = props => {
  const key = idx(props, props => props.profile.id);

  if (key == null) {
    return null;
  }

  return (
    <ModalConsumer>
      {({ queue }) => (
        <TournamentApi.Consumer>
          {api => (
            <StatefulBell
              key={key || ""}
              api={api}
              modalQueue={queue}
              {...props}
            />
          )}
        </TournamentApi.Consumer>
      )}
    </ModalConsumer>
  );
};

export default withData({
  required: ["serverData"],
  optional: ["profile"],
  spinner: "none"
})(_Bell);



// WEBPACK FOOTER //
// ./src/components/Bell/Bell.js